/**
 * TASK-231: Form Data Mapper Service
 *
 * Main service class for mapping extracted document data to all compliance forms.
 * Provides "extract once, populate all" functionality.
 */

import type { KYCFormData } from '../../types/kyc.types';
import type { CompleteRiskProfile } from '../../components/risk-profile/schemas/riskProfileSchema';
import type {
  ClientComplianceData,
  DataSource,
  FormType,
  TrackedField,
  SharedPersonalData,
  SharedContactData,
  SharedEmploymentData,
  SharedFinancialData,
  SharedTaxData,
  SharedPEPData,
  // TASK-261: New shared data interfaces
  SharedIdentificationData,
  SharedPersonalBackgroundData,
  SharedWealthSummaryData,
  SharedAssetBreakdownData,
  SharedAdditionalWealthInfoData,
  SharedIncomeExpectationsData,
  SharedWealthSourceData,
  SharedIncomeSourceData,
  SharedBusinessActivityData,
} from '../../types/clientCompliance';
import { createTrackedField, createDataSource } from '../../types/clientCompliance';
import type {
  ExtractionResult,
  ExtractedClientData,
  FieldWithConfidence,
  ClientProfileData,
  FormAData,
  AllFormsData,
  MapperOptions,
  // TASK-261: New extracted types
  ExtractedIdentification,
  ExtractedPersonalBackground,
  ExtractedWealthSummary,
  ExtractedAssetBreakdown,
  ExtractedAdditionalWealthInfo,
  ExtractedIncomeExpectations,
  ExtractedWealthSource,
  ExtractedIncomeSource,
  ExtractedBusinessActivity,
} from './types';
import { transformDate } from './transformers/dateTransformer';
import { transformAddress } from './transformers/addressTransformer';
// transformCurrency available in transformers/ for form-specific needs
import { mapToKYC, mapFromKYC, calculateAverageConfidence } from './mappers/kycMapper';
import { mapToRiskProfile, mapFromRiskProfile } from './mappers/riskProfileMapper';
import { mapToClientProfile, mapFromClientProfile } from './mappers/clientProfileMapper';
import { mapToFormA, mapFromFormA } from './mappers/formAMapper';

/**
 * FormDataMapper - Main service class
 *
 * Transforms extraction results from document processing into
 * compliance data and individual form formats.
 */
export class FormDataMapper {
  private sourceId: string;
  private sourceName: string;
  private extractedAt: string;

  /**
   * Create a new FormDataMapper instance
   *
   * @param sourceId - Unique identifier for the extraction source (e.g., upload_id)
   * @param sourceName - Human-readable name (e.g., "passport.pdf")
   */
  constructor(sourceId: string, sourceName: string = 'Document Extraction') {
    this.sourceId = sourceId;
    this.sourceName = sourceName;
    this.extractedAt = new Date().toISOString();
  }

  /**
   * Create a DataSource object for this mapper
   */
  private createSource(confidence: number = 1.0): DataSource {
    return createDataSource(
      'document',
      this.sourceId,
      this.sourceName,
      confidence,
      undefined,
      'system'
    );
  }

  /**
   * Create a TrackedField from an extracted field with confidence
   */
  private createTracked<T>(
    field: FieldWithConfidence<T> | undefined
  ): TrackedField<T> | undefined {
    if (!field || field.value === undefined || field.value === null) {
      return undefined;
    }

    return createTrackedField(field.value, this.createSource(field.confidence));
  }

  /**
   * Map extraction result to ClientComplianceData (shared model)
   *
   * @param extraction - Extraction result from backend
   * @returns Partial ClientComplianceData with tracked fields
   */
  mapExtractionToCompliance(extraction: ExtractionResult): Partial<ClientComplianceData> {
    const { client_data } = extraction;

    return {
      // Original sections
      personal: this.mapPersonalData(client_data.personal_info),
      contact: this.mapContactData(client_data.contact_info),
      employment: this.mapEmploymentData(client_data.employment_info),
      financial: this.mapFinancialData(client_data.financial_info),
      tax: this.mapTaxData(client_data.tax_info),
      pep: this.mapPEPData(client_data.pep_info),
      // TASK-261: New sections
      identification: this.mapIdentificationData(client_data.identification),
      personalBackground: this.mapPersonalBackgroundData(client_data.personal_background),
      wealthSummary: this.mapWealthSummaryData(client_data.wealth_summary),
      assetBreakdown: this.mapAssetBreakdownData(client_data.asset_breakdown),
      additionalWealthInfo: this.mapAdditionalWealthInfoData(client_data.additional_wealth_info),
      incomeExpectations: this.mapIncomeExpectationsData(client_data.income_expectations),
      wealthSources: this.mapWealthSourcesData(client_data.wealth_sources),
      incomeSources: this.mapIncomeSourcesData(client_data.income_sources),
      businessActivities: this.mapBusinessActivitiesData(client_data.business_activities),
      // Other
      beneficialOwners: [],
      forms: {},
      screening: {},
      metadata: {
        createdAt: this.extractedAt,
        updatedAt: this.extractedAt,
        version: 1,
      },
    };
  }

  /**
   * Map personal information from extraction
   */
  private mapPersonalData(
    data: ExtractedClientData['personal_info'] | undefined
  ): SharedPersonalData {
    if (!data) return {};

    return {
      firstName: this.createTracked(data.first_name),
      middleName: this.createTracked(data.middle_name),
      lastName: this.createTracked(data.last_name),
      dateOfBirth: data.date_of_birth
        ? this.createTracked({
            value: transformDate(data.date_of_birth.value) || '',
            confidence: data.date_of_birth.confidence,
          })
        : undefined,
      placeOfBirth: this.createTracked(data.place_of_birth),
      gender: this.createTracked(data.gender) as TrackedField<
        'male' | 'female' | 'other' | 'prefer_not_to_say'
      > | undefined,
      maritalStatus: this.createTracked(data.marital_status) as TrackedField<
        'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'domestic_partnership'
      > | undefined,
      nationalities: this.createTracked(data.nationalities),
      languages: this.createTracked(data.languages),
      passportNumber: this.createTracked(data.passport_number),
      nationalId: this.createTracked(data.national_id),
    };
  }

  /**
   * Map contact information from extraction
   */
  private mapContactData(
    data: ExtractedClientData['contact_info'] | undefined
  ): SharedContactData {
    if (!data) return {};

    const residentialAddress = data.address
      ? transformAddress(data.address.value)
      : undefined;
    const mailingAddress = data.mailing_address
      ? transformAddress(data.mailing_address.value)
      : undefined;

    return {
      phonePrimary: this.createTracked(data.phone),
      phoneSecondary: this.createTracked(data.phone_secondary),
      emailPrimary: this.createTracked(data.email),
      emailSecondary: this.createTracked(data.email_secondary),
      residentialAddress: residentialAddress
        ? this.createTracked({
            value: residentialAddress,
            confidence: data.address?.confidence || 0,
          })
        : undefined,
      mailingAddress: mailingAddress
        ? this.createTracked({
            value: mailingAddress,
            confidence: data.mailing_address?.confidence || 0,
          })
        : undefined,
      preferredContactMethod: this.createTracked(data.preferred_contact_method) as TrackedField<
        'email' | 'phone' | 'whatsapp' | 'video_call' | 'in_person'
      > | undefined,
      // TASK-320: Relationship fields
      relationshipReason: this.createTracked(data.relationship_reason),
      relationshipPurpose: this.createTracked(data.relationship_purpose),
    };
  }

  /**
   * Map employment information from extraction
   */
  private mapEmploymentData(
    data: ExtractedClientData['employment_info'] | undefined
  ): SharedEmploymentData {
    if (!data) return {};

    // Map status string to enum
    const statusMap: Record<string, 'employed' | 'selfEmployed' | 'unemployed' | 'retired' | 'student' | 'other'> = {
      employed: 'employed',
      'self-employed': 'selfEmployed',
      selfEmployed: 'selfEmployed',
      unemployed: 'unemployed',
      retired: 'retired',
      student: 'student',
    };

    const mappedStatus = data.status?.value
      ? statusMap[data.status.value.toLowerCase()] || 'other'
      : undefined;

    return {
      employmentStatus: mappedStatus
        ? this.createTracked({
            value: mappedStatus,
            confidence: data.status?.confidence || 0,
          })
        : undefined,
      occupation: this.createTracked(data.occupation),
      employerName: this.createTracked(data.employer),
      employmentIndustry: this.createTracked(data.industry),
      yearsEmployed: this.createTracked(data.years_employed),
    };
  }

  /**
   * Map financial information from extraction
   */
  private mapFinancialData(
    data: ExtractedClientData['financial_info'] | undefined
  ): SharedFinancialData {
    if (!data) return {};

    return {
      totalAssets: this.createTracked(data.total_assets),
      liquidAssets: this.createTracked(data.liquid_assets),
      annualIncome: this.createTracked(data.annual_income),
      totalLiabilities: this.createTracked(data.total_liabilities),
      netWorth: this.createTracked(data.net_worth),
      sourceOfWealth: this.createTracked(data.source_of_wealth),
    };
  }

  /**
   * Map tax information from extraction
   */
  private mapTaxData(
    data: ExtractedClientData['tax_info'] | undefined
  ): SharedTaxData {
    if (!data) return {};

    return {
      taxResidence: this.createTracked(data.residence),
      taxId: this.createTracked(data.tax_id),
      usPerson: this.createTracked(data.us_person),
      fatcaStatus: this.createTracked(data.fatca_status) as TrackedField<
        'compliant' | 'non_compliant' | 'pending' | 'exempt'
      > | undefined,
      taxJurisdictions: this.createTracked(data.tax_jurisdictions),
    };
  }

  /**
   * Map PEP information from extraction
   */
  private mapPEPData(
    data: ExtractedClientData['pep_info'] | undefined
  ): SharedPEPData {
    if (!data) return {};

    return {
      isPep: this.createTracked(data.is_pep),
      isRca: this.createTracked(data.is_rca),
      pepType: this.createTracked(data.pep_type) as TrackedField<
        'domestic' | 'foreign' | 'international'
      > | undefined,
      pepPosition: this.createTracked(data.pep_position),
      pepCountry: this.createTracked(data.pep_country),
      pepStartDate: data.pep_start_date
        ? this.createTracked({
            value: transformDate(data.pep_start_date.value) || '',
            confidence: data.pep_start_date.confidence,
          })
        : undefined,
      pepEndDate: data.pep_end_date
        ? this.createTracked({
            value: transformDate(data.pep_end_date.value) || '',
            confidence: data.pep_end_date.confidence,
          })
        : undefined,
      rcaRelationship: this.createTracked(data.rca_relationship),
      rcaPepName: this.createTracked(data.rca_pep_name),
    };
  }

  // ===========================================================================
  // TASK-261: NEW MAPPING METHODS
  // ===========================================================================

  /**
   * Map identification data from extraction
   */
  private mapIdentificationData(
    data: ExtractedIdentification | undefined
  ): SharedIdentificationData | undefined {
    if (!data) return undefined;

    return {
      passportNumber: this.createTracked(data.passport_number),
      passportExpiry: data.passport_expiry
        ? this.createTracked({
            value: transformDate(data.passport_expiry.value) || '',
            confidence: data.passport_expiry.confidence,
          })
        : undefined,
      passportCountry: this.createTracked(data.passport_country),
      idCardNumber: this.createTracked(data.id_card_number),
      ahvNumber: this.createTracked(data.ahv_number),
    };
  }

  /**
   * Map personal background data from extraction
   */
  private mapPersonalBackgroundData(
    data: ExtractedPersonalBackground | undefined
  ): SharedPersonalBackgroundData | undefined {
    if (!data) return undefined;

    return {
      educationLevel: this.createTracked(data.education_level),
      institutionsAttended: this.createTracked(data.institutions_attended),
      professionalCertifications: this.createTracked(data.professional_certifications),
      hobbiesInterests: this.createTracked(data.hobbies_interests),
      familyComposition: this.createTracked(data.family_composition),
      dependentsCount: this.createTracked(data.dependents_count),
      spouseEmploymentStatus: this.createTracked(data.spouse_employment_status),
    };
  }

  /**
   * Map wealth summary data from extraction
   */
  private mapWealthSummaryData(
    data: ExtractedWealthSummary | undefined
  ): SharedWealthSummaryData | undefined {
    if (!data) return undefined;

    return {
      netWorth: this.createTracked(data.net_worth),
      liquidNetWorth: this.createTracked(data.liquid_net_worth),
      netWorthCurrency: this.createTracked(data.net_worth_currency),
      sourceOfWealth: this.createTracked(data.source_of_wealth),
      sourceOfFunds: this.createTracked(data.source_of_funds),
      primaryWealthSource: this.createTracked(data.primary_wealth_source),
      wealthNarrative: this.createTracked(data.wealth_narrative),
    };
  }

  /**
   * Map asset breakdown data from extraction
   */
  private mapAssetBreakdownData(
    data: ExtractedAssetBreakdown | undefined
  ): SharedAssetBreakdownData | undefined {
    if (!data) return undefined;

    return {
      cash: this.createTracked(data.cash),
      securities: this.createTracked(data.securities),
      real_estate: this.createTracked(data.real_estate),
      business_equity: this.createTracked(data.business_equity),
      commodities: this.createTracked(data.commodities),
      crypto: this.createTracked(data.crypto),
      other: this.createTracked(data.other),
    };
  }

  /**
   * Map additional wealth info from extraction
   */
  private mapAdditionalWealthInfoData(
    data: ExtractedAdditionalWealthInfo | undefined
  ): SharedAdditionalWealthInfoData | undefined {
    if (!data) return undefined;

    return {
      inheritanceExpected: this.createTracked(data.inheritance_expected),
      inheritanceDetails: this.createTracked(data.inheritance_details),
      unusualTransactions: this.createTracked(data.unusual_transactions),
      unusualTransactionDetails: this.createTracked(data.unusual_transaction_details),
      sourceDocumentation: this.createTracked(data.source_documentation),
    };
  }

  /**
   * Map income expectations from extraction
   */
  private mapIncomeExpectationsData(
    data: ExtractedIncomeExpectations | undefined
  ): SharedIncomeExpectationsData | undefined {
    if (!data) return undefined;

    return {
      expectedIncomeChange: this.createTracked(data.expected_income_change),
      expectedChangeDetails: this.createTracked(data.expected_change_details),
      retirementIncome: this.createTracked(data.retirement_income),
      retirementIncomeDetails: this.createTracked(data.retirement_income_details),
      otherFinancialSupport: this.createTracked(data.other_financial_support),
      otherSupportDetails: this.createTracked(data.other_support_details),
    };
  }

  /**
   * Map wealth sources array from extraction
   */
  private mapWealthSourcesData(
    data: ExtractedWealthSource[] | undefined
  ): SharedWealthSourceData[] | undefined {
    if (!data || !Array.isArray(data) || data.length === 0) return undefined;

    return data.map((source) => ({
      sourceType: this.createTracked(source.source_type),
      sourceDescription: this.createTracked(source.source_description),
      percentage: this.createTracked(source.percentage),
      documented: this.createTracked(source.documented),
      acquisitionPeriod: this.createTracked(source.acquisition_period),
      assetValue: this.createTracked(source.asset_value),
      assetCurrency: this.createTracked(source.asset_currency),
      assetLocation: this.createTracked(source.asset_location),
      riskLevel: this.createTracked(source.risk_level),
    }));
  }

  /**
   * Map income sources array from extraction
   */
  private mapIncomeSourcesData(
    data: ExtractedIncomeSource[] | undefined
  ): SharedIncomeSourceData[] | undefined {
    if (!data || !Array.isArray(data) || data.length === 0) return undefined;

    return data.map((source) => ({
      incomeType: this.createTracked(source.income_type),
      incomeDescription: this.createTracked(source.income_description),
      annualAmount: this.createTracked(source.annual_amount),
      currency: this.createTracked(source.currency),
      frequency: this.createTracked(source.frequency),
      sourceName: this.createTracked(source.source_name),
      sourceCountry: this.createTracked(source.source_country),
      verified: this.createTracked(source.verified),
      stabilityRating: this.createTracked(source.stability_rating),
    }));
  }

  /**
   * Map business activities array from extraction
   */
  private mapBusinessActivitiesData(
    data: ExtractedBusinessActivity[] | undefined
  ): SharedBusinessActivityData[] | undefined {
    if (!data || !Array.isArray(data) || data.length === 0) return undefined;

    return data.map((activity) => ({
      businessName: this.createTracked(activity.business_name),
      businessType: this.createTracked(activity.business_type),
      registrationNumber: this.createTracked(activity.registration_number),
      registrationCountry: this.createTracked(activity.registration_country),
      primaryIndustry: this.createTracked(activity.primary_industry),
      ownershipPercentage: this.createTracked(activity.ownership_percentage),
      ownershipType: this.createTracked(activity.ownership_type),
      yearsInBusiness: this.createTracked(activity.years_in_business),
      annualRevenue: this.createTracked(activity.annual_revenue),
      cashIntensive: this.createTracked(activity.cash_intensive),
    }));
  }

  /**
   * Map shared compliance data to a specific form type
   *
   * @param formType - Target form type
   * @param data - Shared compliance data
   * @param options - Mapper options
   * @returns Form-specific data
   */
  mapToForm<T>(
    formType: FormType,
    data: ClientComplianceData,
    options?: MapperOptions
  ): Partial<T> {
    switch (formType) {
      case 'kyc':
        return mapToKYC(data, options) as Partial<T>;
      case 'riskProfile':
        return mapToRiskProfile(data, options) as Partial<T>;
      case 'clientProfile':
        return mapToClientProfile(data, options) as Partial<T>;
      case 'formA':
        return mapToFormA(data, options) as Partial<T>;
      default:
        throw new Error(`Unknown form type: ${formType}`);
    }
  }

  /**
   * Map form data back to shared compliance data
   *
   * @param formType - Source form type
   * @param formData - Form-specific data
   * @param source - Data source for tracking
   * @returns Partial shared compliance data
   */
  mapFromForm(
    formType: FormType,
    formData: unknown,
    source: DataSource
  ): Partial<ClientComplianceData> {
    switch (formType) {
      case 'kyc':
        return mapFromKYC(formData as Partial<KYCFormData>, source);
      case 'riskProfile':
        return mapFromRiskProfile(formData as Partial<CompleteRiskProfile>, source);
      case 'clientProfile':
        return mapFromClientProfile(formData as Partial<ClientProfileData>, source);
      case 'formA':
        return mapFromFormA(formData as Partial<FormAData>, source);
      default:
        throw new Error(`Unknown form type: ${formType}`);
    }
  }

  /**
   * Map shared compliance data to all forms at once
   *
   * @param data - Shared compliance data
   * @param options - Mapper options
   * @returns All forms data
   */
  mapToAllForms(data: ClientComplianceData, options?: MapperOptions): AllFormsData {
    return {
      kyc: mapToKYC(data, options),
      riskProfile: mapToRiskProfile(data, options),
      clientProfile: mapToClientProfile(data, options),
      formA: mapToFormA(data, options),
    };
  }

  /**
   * Calculate overall confidence score from extraction
   */
  static calculateOverallConfidence(extraction: ExtractionResult): number {
    return extraction.confidence || 0;
  }

  /**
   * Calculate average confidence from compliance data
   */
  static calculateAverageConfidence(data: ClientComplianceData): number {
    return calculateAverageConfidence(data);
  }

  /**
   * Get extraction metadata
   */
  getMetadata(): { sourceId: string; sourceName: string; extractedAt: string } {
    return {
      sourceId: this.sourceId,
      sourceName: this.sourceName,
      extractedAt: this.extractedAt,
    };
  }
}

/**
 * Create a FormDataMapper instance from an extraction result
 */
export function createMapperFromExtraction(extraction: ExtractionResult): FormDataMapper {
  return new FormDataMapper(
    extraction.upload_id,
    `Document Extraction (${new Date(extraction.extraction_timestamp).toLocaleDateString()})`
  );
}

/**
 * Quick helper: Map extraction directly to all forms
 */
export function mapExtractionToAllForms(
  extraction: ExtractionResult,
  options?: MapperOptions
): { compliance: Partial<ClientComplianceData>; forms: AllFormsData } {
  const mapper = createMapperFromExtraction(extraction);
  const compliance = mapper.mapExtractionToCompliance(extraction);

  // Create a full ClientComplianceData for form mapping
  const fullCompliance: ClientComplianceData = {
    clientId: extraction.upload_id,
    // Original sections
    personal: compliance.personal || {},
    contact: compliance.contact || {},
    employment: compliance.employment || {},
    financial: compliance.financial || {},
    tax: compliance.tax || {},
    pep: compliance.pep || {},
    // TASK-261: New sections
    identification: compliance.identification,
    personalBackground: compliance.personalBackground,
    wealthSummary: compliance.wealthSummary,
    assetBreakdown: compliance.assetBreakdown,
    additionalWealthInfo: compliance.additionalWealthInfo,
    incomeExpectations: compliance.incomeExpectations,
    wealthSources: compliance.wealthSources,
    incomeSources: compliance.incomeSources,
    businessActivities: compliance.businessActivities,
    // Other
    beneficialOwners: compliance.beneficialOwners || [],
    forms: {},
    screening: {},
    metadata: compliance.metadata || {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
    },
  };

  return {
    compliance,
    forms: mapper.mapToAllForms(fullCompliance, options),
  };
}
